// Snapping and tracing for the boundary editor: the geometry, with no map in
// it, so it can be tested.
//
// WHY SNAP AT ALL. Two territories share a line only when they share the
// COORDINATE (territoryedit's keyOf), and a hand-dragged vertex lands on the
// exact pixel the hand chose — a hair off the neighbour, which the server then
// has to resolve as a sliver of gained or vacated ground. Snapping makes
// "on the line" mean the same thing the model means by it.
//
// WHY TRACE. A front that now follows a coast or an existing district edge is
// a RUN of vertices, not one. Dragging them one at a time is the operation the
// data already has an answer for: the guide line's own vertices. Trace grafts
// that run into the outline in one go.
//
// Screen coordinates decide what is NEAR (12 px is 12 px at any zoom, which is
// what a hand means by "on it"); ground coordinates are what gets stored, and
// a snap to a VERTEX stores that vertex's own coordinate verbatim rather than
// a round-trip through the screen — bit-identical is what makes it shared.

export type Pt = [number, number];
export interface ScreenPt { x: number; y: number }

/** What a guide is: an outline of a territory, a ring of the country
 *  boundary, admin-unit linework (CGAZ or the detailed gbOpen set), or a
 *  road. */
export type GuideSource = 'territory' | 'boundary' | 'admin' | 'road';

/** A line the editor can snap or trace along. `pts` is ground, `screen` is
 *  the same points projected, and a closed guide wraps from its last point to
 *  its first. */
export interface Guide {
  pts: Pt[];
  screen: ScreenPt[];
  closed: boolean;
  source: GuideSource;
}

/** Where on a guide something landed: a segment and how far along it. */
export interface GuidePos {
  guide: number;
  seg: number;
  t: number;
}

export interface SnapHit extends GuidePos {
  /** Ground position to move to. */
  pos: Pt;
  screen: ScreenPt;
  kind: 'vertex' | 'edge';
  source: GuideSource;
  distPx: number;
}

/** Grid buckets of segments, so a pointermove costs a handful of distance
 *  checks instead of a walk over a country's worth of vertices. */
export interface SnapIndex {
  guides: Guide[];
  cell: number;
  cells: Map<string, [number, number][]>;   // cell key -> [guide, segment]
}

const key = (cx: number, cy: number): string => `${cx}:${cy}`;

/** Perpendicular distance from a point to a segment, and where along it the
 *  foot of that perpendicular falls (clamped to the segment). */
export function segClosest(p: ScreenPt, a: ScreenPt, b: ScreenPt):
{ t: number; x: number; y: number; dist: number } {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2));
  const x = a.x + t * dx;
  const y = a.y + t * dy;
  return { t, x, y, dist: Math.hypot(p.x - x, p.y - y) };
}

const lerp = (a: Pt, b: Pt, t: number): Pt =>
  [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];

const segCount = (g: Guide): number => (g.closed ? g.pts.length : g.pts.length - 1);
const at = <T>(list: T[], i: number): T => list[i % list.length]!;

export function buildSnapIndex(guides: Guide[], cell = 64): SnapIndex {
  const cells = new Map<string, [number, number][]>();
  guides.forEach((g, gi) => {
    const n = segCount(g);
    for (let s = 0; s < n; s++) {
      const a = at(g.screen, s);
      const b = at(g.screen, s + 1);
      const x0 = Math.floor(Math.min(a.x, b.x) / cell);
      const x1 = Math.floor(Math.max(a.x, b.x) / cell);
      const y0 = Math.floor(Math.min(a.y, b.y) / cell);
      const y1 = Math.floor(Math.max(a.y, b.y) / cell);
      // A segment longer than the screen would bucket into thousands of
      // cells for nothing — the ends are what a hand aims at, and the middle
      // is still found from whichever cell the cursor is in.
      if ((x1 - x0) * (y1 - y0) > 4096) continue;
      for (let cx = x0; cx <= x1; cx++) {
        for (let cy = y0; cy <= y1; cy++) {
          const k = key(cx, cy);
          const bucket = cells.get(k);
          if (bucket) bucket.push([gi, s]);
          else cells.set(k, [[gi, s]]);
        }
      }
    }
  });
  return { guides, cell, cells };
}

/** The strongest snap within `radiusPx`, or null.
 *
 *  A VERTEX always beats an edge inside the radius, however much closer the
 *  edge is: a hand near a corner means the corner, and landing on the corner
 *  is what makes the two outlines share it.
 *
 *  `vertices: false` snaps to edges only — the push brush's choice: a brush
 *  moves a run of vertices at once, and letting each grab the nearest CORNER
 *  would pile neighbours onto one coordinate. */
export function nearestSnap(index: SnapIndex, p: ScreenPt, radiusPx: number,
                            reject?: (hit: SnapHit) => boolean,
                            opts: { vertices?: boolean } = {}): SnapHit | null {
  const wantVertices = opts.vertices !== false;
  const { cell, guides } = index;
  const reach = Math.ceil(radiusPx / cell);
  const cx = Math.floor(p.x / cell);
  const cy = Math.floor(p.y / cell);
  let bestVertex: SnapHit | null = null;
  let bestEdge: SnapHit | null = null;
  const seen = new Set<string>();

  for (let dx = -reach; dx <= reach; dx++) {
    for (let dy = -reach; dy <= reach; dy++) {
      for (const [gi, s] of index.cells.get(key(cx + dx, cy + dy)) ?? []) {
        const mark = `${gi}:${s}`;
        if (seen.has(mark)) continue;
        seen.add(mark);
        const g = guides[gi]!;
        const a = at(g.screen, s);
        const b = at(g.screen, s + 1);
        const hit = segClosest(p, a, b);
        if (hit.dist > radiusPx) continue;

        // Either end of the segment, if the pointer is nearer that than the
        // line itself.
        for (const [vi, v] of (wantVertices ? [[s, a], [s + 1, b]] : []) as [number, ScreenPt][]) {
          const d = Math.hypot(p.x - v.x, p.y - v.y);
          if (d > radiusPx || (bestVertex && d >= bestVertex.distPx)) continue;
          const idx = vi % g.pts.length;
          const cand: SnapHit = {
            guide: gi, seg: s, t: vi === s ? 0 : 1, pos: g.pts[idx]!,
            screen: v, kind: 'vertex', source: g.source, distPx: d,
          };
          if (reject?.(cand)) continue;
          bestVertex = cand;
        }
        if (!bestEdge || hit.dist < bestEdge.distPx) {
          const cand: SnapHit = {
            guide: gi, seg: s, t: hit.t,
            pos: lerp(at(g.pts, s), at(g.pts, s + 1), hit.t),
            screen: { x: hit.x, y: hit.y },
            kind: 'edge', source: g.source, distPx: hit.dist,
          };
          if (!reject?.(cand)) bestEdge = cand;
        }
      }
    }
  }
  return bestVertex ?? bestEdge;
}

/** A position on a guide as one number: segment index plus how far along it. */
const posOf = (q: GuidePos): number => q.seg + q.t;

const ptAtPos = (g: Guide, p: number): Pt => {
  const i = Math.floor(p);
  return lerp(at(g.pts, i), at(g.pts, i + 1), p - i);
};
const screenAtPos = (g: Guide, p: number): ScreenPt => {
  const i = Math.floor(p);
  const a = at(g.screen, i);
  const b = at(g.screen, i + 1);
  const t = p - i;
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
};

/** Walk a guide from one position to another in one direction, collecting the
 *  vertices passed plus both interpolated ends. Wrapping is handled by
 *  measuring every vertex as a distance travelled from the start, so a closed
 *  guide reads the same whether or not the run crosses its seam. */
function collect(g: Guide, from: number, to: number, forward: boolean):
{ pts: Pt[]; px: number } {
  const n = segCount(g);
  const reach = (x: number): number => {
    const d = forward ? x - from : from - x;
    if (!g.closed) return d;
    return ((d % n) + n) % n;
  };
  const target = reach(to);
  const pts: Pt[] = [ptAtPos(g, from)];
  const screen: ScreenPt[] = [screenAtPos(g, from)];
  const step = forward ? 1 : -1;
  let i = forward ? Math.floor(from) + 1 : Math.floor(from);
  for (let guard = 0; guard <= n; guard++, i += step) {
    const here = reach(i);
    if (here >= target || (!g.closed && (i < 0 || i > n))) break;
    const idx = ((i % g.pts.length) + g.pts.length) % g.pts.length;
    pts.push(g.pts[idx]!);
    screen.push(g.screen[idx]!);
  }
  pts.push(ptAtPos(g, to));
  screen.push(screenAtPos(g, to));
  let px = 0;
  for (let k = 1; k < screen.length; k++) {
    px += Math.hypot(screen[k]!.x - screen[k - 1]!.x, screen[k]!.y - screen[k - 1]!.y);
  }
  return { pts: dedupe(pts), px };
}

/** Drop a point that repeats the one before it — an end landing exactly on a
 *  vertex would otherwise store it twice. */
function dedupe(pts: Pt[]): Pt[] {
  return pts.filter((p, i) => i === 0 || p[0] !== pts[i - 1]![0] || p[1] !== pts[i - 1]![1]);
}

/** The ground points from one place on a guide to another, both ends
 *  included. On a closed guide, the SHORTER way round — you clicked two points
 *  on a coast because you meant the coast between them, not the rest of it. */
export function runBetween(g: Guide, from: GuidePos, to: GuidePos): Pt[] {
  const a = posOf(from);
  const b = posOf(to);
  if (!g.closed) return collect(g, a, b, a <= b).pts;
  const forward = collect(g, a, b, true);
  const backward = collect(g, a, b, false);
  return (forward.px <= backward.px ? forward : backward).pts;
}

/** Distance from a point to a polyline, in degrees — comparison only. */
function distToRun(p: Pt, run: Pt[]): number {
  let best = Infinity;
  for (let i = 1; i < run.length; i++) {
    const hit = segClosest({ x: p[0], y: p[1] },
                           { x: run[i - 1]![0], y: run[i - 1]![1] },
                           { x: run[i]![0], y: run[i]![1] });
    if (hit.dist < best) best = hit.dist;
  }
  return best;
}

/** Which way round the ring the traced run belongs.
 *
 *  Not "the shorter span" — pulling a long front onto a coast replaces a long
 *  span. The run PARALLELS the path it is replacing, so the span whose own
 *  points sit closer to the run is the one that was traced over. */
export function chooseSide(ring: Pt[], ia: number, ib: number, run: Pt[]): 'forward' | 'backward' {
  const n = ring.length;
  const sample = (from: number, to: number): number => {
    const len = ((to - from) + n) % n;
    if (len <= 1) return Infinity;          // an empty span parallels nothing
    const step = Math.max(1, Math.floor(len / 24));
    let total = 0;
    let count = 0;
    for (let k = 1; k < len; k += step) {
      total += distToRun(ring[(from + k) % n]!, run);
      count += 1;
    }
    return count ? total / count : Infinity;
  };
  const forward = sample(ia, ib);
  const backward = sample(ib, ia);
  if (forward === Infinity && backward === Infinity) return 'forward';
  return forward <= backward ? 'forward' : 'backward';
}

/** Replace the ring's path between two of its vertices with `run`.
 *
 *  The run's own ends become the outline's — the point of tracing is that the
 *  new line IS the guide, so the old vertices at each end go with the span.
 *  Returns null when the result would not be a ring any more. */
export function graftRun(ring: Pt[], ia: number, ib: number, run: Pt[],
                         side: 'forward' | 'backward'): Pt[] | null {
  const n = ring.length;
  if (n < 3 || run.length < 2 || ia === ib) return null;
  const [from, to] = side === 'forward' ? [ia, ib] : [ib, ia];
  const oriented = side === 'forward' ? run : [...run].reverse();

  // Everything the span did not cover, walked forward from just after it.
  const keep: Pt[] = [];
  for (let k = 1; ; k++) {
    const i = (to + k) % n;
    if (i === from) break;
    keep.push(ring[i]!);
    if (k > n) return null;              // ring was malformed
  }
  const out = [...oriented, ...keep];
  return out.length >= 3 ? out : null;
}

// --- tracing across a NETWORK of lines -----------------------------------------
//
// runBetween follows ONE guide. Admin linework and roads are not one guide:
// dissolved district edges break at every junction where three districts
// meet, and a road is a way per stretch between intersections. A front that
// follows the Marib–Sirwah road and then a district edge is a run across
// several of them, so a trace whose two ends land on DIFFERENT guides walks
// the network instead — the shortest path along the lines, joined where they
// share an exact coordinate (a junction node of a road, a triple point of the
// dissolved linework, and — because the lines come from the same boundary
// set — a territory outline built from CGAZ districts meeting CGAZ linework).

const groundDist = (a: Pt, b: Pt): number => {
  const k = Math.cos(((a[1] + b[1]) / 2) * Math.PI / 180);
  return Math.hypot((b[0] - a[0]) * k, b[1] - a[1]);
};

/** A tiny binary min-heap keyed by priority. */
class Heap {
  private keys: string[] = [];
  private pri: number[] = [];
  get size(): number { return this.keys.length; }
  push(key: string, p: number): void {
    this.keys.push(key); this.pri.push(p);
    let i = this.keys.length - 1;
    while (i > 0) {
      const up = (i - 1) >> 1;
      if (this.pri[up]! <= this.pri[i]!) break;
      this.swap(i, up); i = up;
    }
  }
  pop(): string | undefined {
    const n = this.keys.length;
    if (!n) return undefined;
    const top = this.keys[0];
    this.swap(0, n - 1);
    this.keys.pop(); this.pri.pop();
    let i = 0;
    for (;;) {
      const l = 2 * i + 1;
      const r = l + 1;
      let m = i;
      if (l < this.keys.length && this.pri[l]! < this.pri[m]!) m = l;
      if (r < this.keys.length && this.pri[r]! < this.pri[m]!) m = r;
      if (m === i) break;
      this.swap(i, m); i = m;
    }
    return top;
  }
  private swap(a: number, b: number): void {
    [this.keys[a], this.keys[b]] = [this.keys[b]!, this.keys[a]!];
    [this.pri[a], this.pri[b]] = [this.pri[b]!, this.pri[a]!];
  }
}

/** The shortest run along the guides from one hit to another, both ends
 *  included, or null when no chain of shared coordinates connects them.
 *
 *  Both hits on the SAME guide use runBetween — the existing, exact walk
 *  along one line (a closed outline's shorter way round) — because a network
 *  path could shortcut across another line, and a trace along one line means
 *  that line. `include` narrows which other guides the network may use. */
export function traceNetwork(guides: Guide[], from: SnapHit, to: SnapHit,
                             include?: (g: Guide, index: number) => boolean,
                             maxNodes = 400_000): Pt[] | null {
  if (from.guide === to.guide) {
    const g = guides[from.guide];
    return g ? runBetween(g, from, to) : null;
  }
  const key = (p: Pt): string => `${p[0]},${p[1]}`;
  const coords = new Map<string, Pt>();
  const adj = new Map<string, [string, number][]>();
  const link = (a: string, b: string, w: number): void => {
    if (a === b) return;
    (adj.get(a) ?? adj.set(a, []).get(a)!).push([b, w]);
    (adj.get(b) ?? adj.set(b, []).get(b)!).push([a, w]);
  };
  guides.forEach((g, gi) => {
    if (gi !== from.guide && gi !== to.guide && include && !include(g, gi)) return;
    const n = segCount(g);
    for (let s = 0; s < n; s++) {
      const a = at(g.pts, s);
      const b = at(g.pts, s + 1);
      const ka = key(a);
      const kb = key(b);
      coords.set(ka, a);
      coords.set(kb, b);
      link(ka, kb, groundDist(a, b));
    }
  });
  if (coords.size > maxNodes) return null;
  // The two ends are nodes of their own, part-way along their segments.
  const F = ' from';
  const T = ' to';
  const hang = (node: string, hit: SnapHit): void => {
    const g = guides[hit.guide]!;
    const a = at(g.pts, hit.seg);
    const b = at(g.pts, hit.seg + 1);
    coords.set(node, hit.pos);
    link(node, key(a), groundDist(hit.pos, a));
    link(node, key(b), groundDist(hit.pos, b));
  };
  hang(F, from);
  hang(T, to);

  // A* over ground distance: the straight line to the end never overestimates.
  const goal = to.pos;
  const dist = new Map<string, number>([[F, 0]]);
  const prev = new Map<string, string>();
  const done = new Set<string>();
  const heap = new Heap();
  heap.push(F, groundDist(from.pos, goal));
  while (heap.size) {
    const u = heap.pop()!;
    if (done.has(u)) continue;
    if (u === T) break;
    done.add(u);
    const du = dist.get(u)!;
    for (const [v, w] of adj.get(u) ?? []) {
      if (done.has(v)) continue;
      const dv = du + w;
      if (dv < (dist.get(v) ?? Infinity)) {
        dist.set(v, dv);
        prev.set(v, u);
        heap.push(v, dv + groundDist(coords.get(v)!, goal));
      }
    }
  }
  if (!prev.has(T)) return null;
  const path: Pt[] = [];
  for (let k: string | undefined = T; k !== undefined; k = prev.get(k)) {
    path.push(coords.get(k)!);
    if (k === F) break;
  }
  path.reverse();
  return dedupe(path);
}

/** How much of the push a vertex at distance `d` from the brush centre takes:
 *  all of it at the centre, none at the rim, smooth at both ends — so the line
 *  bends into the push instead of kinking at the brush's edge. */
export function falloff(d: number, r: number): number {
  if (r <= 0 || d >= r) return 0;
  const q = 1 - (d / r) * (d / r);
  return q * q;
}

/** The ring vertex nearest a ground point — where a traced run is grafted on.
 *  Ground distance is fine here: the two candidates are never close. */
export function nearestVertex(ring: Pt[], p: Pt): number {
  let best = 0;
  let bestD = Infinity;
  for (let i = 0; i < ring.length; i++) {
    const d = Math.hypot(ring[i]![0] - p[0], ring[i]![1] - p[1]);
    if (d < bestD) { bestD = d; best = i; }
  }
  return best;
}
